import JSZip from "jszip";
import { utils as XlsxUtils, write as writeXlsx } from "xlsx";
import { Localization } from "@viamap/viamap2-common";
import { getANNLLMAnalyzer } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { PDFDocument } from "pdf-lib";
import { buildNumberedBundlePdf, type BundleSection } from "src/managers/TingbogBundlePdf";
import { renderPdfPageToImage } from "src/modules/ANNModule/ANNPDFPageToImage";
import { loadPaategningBilagFiles } from "src/managers/TingbogPaategningPdf";
import { getServitutBilagDownloadFileName } from "src/managers/ServitutDownloadNaming";
import { addServitutPackageToZip } from "src/propertyInfoTemplates/TingbogServitutBatch";
import { selectExportFileName, selectFieldText, selectHeading } from "../store/selectors";
import type { ServitutExportProgress } from "../model/report";
import { isDigitalRow, isUserFileDocument, type ServitutRow } from "../model/row";
import { cutExportPdf, getRowSourcePdfBlob, rowTingbogDokumentId, withRowPaategningPages } from "./rowIo";
import { buildUnavailableServitutTxt, shouldExportSourceDocument, toTxtFileName } from "./exportFillers";

export type ServitutExportMode = "excel" | "zip" | "pdf" | "bundle";
export { buildServitutPdfBlob, buildServitutPdfModel } from "./exportPdf";

function uniqueZipName(fileName: string, usedNames: Set<string>): string {
   const trimmed = fileName.trim() || "servitut.pdf";
   if (!usedNames.has(trimmed)) {
      usedNames.add(trimmed);
      return trimmed;
   }
   const match = /^(.*?)(\.[^.]+)?$/.exec(trimmed);
   const baseName = match?.[1] || "servitut";
   const extension = match?.[2] || "";
   let index = 2;
   while (usedNames.has(`${baseName} (${index})${extension}`)) {
      index += 1;
   }
   const uniqueName = `${baseName} (${index})${extension}`;
   usedNames.add(uniqueName);
   return uniqueName;
}

export function buildServitutExcelBlob(
   rows: ServitutRow[],
   columns: number[],
   columnLabels: Record<string, string>,
   options?: { includeAnalysisFields?: boolean; caseDescription?: string },
): Blob {
   const includeAnalysisFields = options?.includeAnalysisFields === true;
   const caseDescription = options?.caseDescription || "";
   const records = rows.map((row) => {
      const record: Record<string, string | number> = {
         "Prioritet": row.landRegistry.priority,
         "Dato-løbenummer": row.identity.datoloebenr || row.landRegistry.displayAktId,
         "Servituttekst": selectHeading(row),
      };
      if (includeAnalysisFields) {
         record["Resume"] = selectFieldText(row, "resume", caseDescription);
         record["Servituttype"] = selectFieldText(row, "condition", caseDescription);
         record["Påtaleberettiget"] = selectFieldText(row, "entitledParty", caseDescription);
         record["Redegørelse"] = selectFieldText(row, "statement", caseDescription);
      }
      columns.forEach((bfe) => {
         record[columnLabels[String(bfe)] || String(bfe)] = row.landRegistry.includedBfes.includes(bfe) ? "x" : "";
      });
      return record;
   });
   const worksheet = XlsxUtils.json_to_sheet(records);
   const workbook = XlsxUtils.book_new();
   XlsxUtils.book_append_sheet(workbook, worksheet, "Servitutredegørelse");
   const buffer = writeXlsx(workbook, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
   return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

export async function buildServitutZip(
   rows: ServitutRow[],
   columns: number[],
   columnLabels: Record<string, string>,
   onProgress?: (state: ServitutExportProgress) => void | Promise<void>,
   options?: { includeAnalysisFields?: boolean; caseDescription?: string },
): Promise<Blob> {
   const zip = new JSZip();
   const usedNames = new Set<string>();
   const analyzer = getANNLLMAnalyzer();
   const totalSteps = 2 + rows.length;
   let completedSteps = 0;
   const emit = async (state: ServitutExportProgress) => {
      await onProgress?.(state);
   };

   await emit({ completedSteps, totalSteps, stage: "spreadsheet", activeRowKey: null });
   zip.file(
      uniqueZipName("Servitutredegørelse.xlsx", usedNames),
      buildServitutExcelBlob(rows, columns, columnLabels, options),
   );
   completedSteps += 1;
   await emit({ completedSteps, totalSteps, stage: "spreadsheet", activeRowKey: null });

   for (let docIndex = 0; docIndex < rows.length; docIndex += 1) {
      const row = rows[docIndex];
      await emit({
         completedSteps,
         totalSteps,
         stage: "document",
         activeRowKey: row.id,
         documentIndex: docIndex + 1,
         documentTotal: rows.length,
      });
      const baseFileName = selectExportFileName(row);
      if (!shouldExportSourceDocument(row)) {
         zip.file(uniqueZipName(toTxtFileName(baseFileName), usedNames), buildUnavailableServitutTxt(row));
         completedSteps += 1;
         continue;
      }
      if (isDigitalRow(row) && row.document.kind !== "upload" && row.document.kind !== "annotation") {
         const electronic = row.document.kind === "tingbog_digital"
            ? row.document.electronicServitut
            : row.registryBinding?.kind === "tingbog_digital"
               ? row.registryBinding.electronicServitut
               : row.landRegistry.servitut;
         if (!electronic) {
            zip.file(
               uniqueZipName(toTxtFileName(baseFileName), usedNames),
               `Digital servitutpakke ikke tilgængelig (${row.landRegistry.displayAktId}).\n${row.landRegistry.summary || ""}`,
            );
         } else {
            try {
               await addServitutPackageToZip(zip, electronic);
            } catch (error) {
               zip.file(
                  uniqueZipName(toTxtFileName(baseFileName), usedNames),
                  `Kunne ikke hente digital servitutpakke: ${error instanceof Error ? error.message : String(error)}\n\n${row.landRegistry.summary || "Ingen servituttekst."}`,
               );
            }
         }
      } else {
         try {
            const pdfBlob = row.document.kind === "annotation" || row.document.kind === "upload"
               ? await getRowSourcePdfBlob(row)
               : (row.document.kind === "tingbog_akt"
                  ? await analyzer.fetchPdf(row.document.aktId)
                  : await getRowSourcePdfBlob(row));
            if (!pdfBlob) {
               throw new Error("PDF mangler");
            }
            const exportedPdf = await withRowPaategningPages(await cutExportPdf(row, pdfBlob), row);
            zip.file(uniqueZipName(baseFileName, usedNames), exportedPdf);
            const dokumentId = rowTingbogDokumentId(row);
            if (dokumentId) {
               const bilagFiles = await loadPaategningBilagFiles(dokumentId);
               for (let index = 0; index < bilagFiles.length; index += 1) {
                  zip.file(
                     uniqueZipName(
                        getServitutBilagDownloadFileName(baseFileName, index + 1, bilagFiles[index].name),
                        usedNames,
                     ),
                     bilagFiles[index].blob,
                  );
               }
            }
         } catch (error) {
            zip.file(
               uniqueZipName(toTxtFileName(baseFileName), usedNames),
               `Could not find servitut: ${row.landRegistry.displayAktId}\n${error instanceof Error ? error.message : String(error)}\n\n${row.landRegistry.summary || ""}`,
            );
         }
      }
      completedSteps += 1;
   }

   await emit({ completedSteps, totalSteps, stage: "compress", activeRowKey: null });
   const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
   completedSteps += 1;
   await emit({ completedSteps, totalSteps, stage: "compress", activeRowKey: null });
   return blob;
}

/** Some Tingbog PDFs use a page tree pdf-lib cannot copy. Draw those pages so the servitut still lands in the bundle. */
async function rewritePdfForBundle(blob: Blob): Promise<Blob> {
   try {
      const source = await PDFDocument.load(await blob.arrayBuffer(), { ignoreEncryption: true });
      const output = await PDFDocument.create();
      const pages = await output.copyPages(source, source.getPageIndices());
      if (!pages.length) {
         return blob;
      }
      pages.forEach((page) => output.addPage(page));
      return new Blob([new Uint8Array(await output.save())], { type: "application/pdf" });
   } catch (error) {
      console.warn("[Servitutredegørelse] copying servitut PDF failed, drawing its pages", error);
   }
   const [{ pdfjs }, worker] = await Promise.all([
      import("react-pdf"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
   ]);
   pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
   const pdf = await pdfjs.getDocument({ data: await blob.arrayBuffer() }).promise;
   const output = await PDFDocument.create();
   try {
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
         const png = await renderPdfPageToImage(blob, pageNumber, 1.5);
         const image = await output.embedPng(new Uint8Array(await png.arrayBuffer()));
         const page = output.addPage([image.width, image.height]);
         page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
      }
   } finally {
      await pdf.destroy();
   }
   return new Blob([new Uint8Array(await output.save())], { type: "application/pdf" });
}

async function pdfForBundle(row: ServitutRow): Promise<Blob | null> {
   if (!shouldExportSourceDocument(row)) {
      return null;
   }
   const blob = await getRowSourcePdfBlob(row);
   if (!blob || blob.size < 8) {
      return null;
   }
   let servitutPdf: Blob | null = blob;
   if (isDigitalRow(row) && !isUserFileDocument(row)) {
      servitutPdf = blob;
   } else if (row.document.kind !== "annotation" && row.document.kind !== "upload" && row.document.kind !== "tingbog_akt") {
      servitutPdf = null;
   } else if (row.document.kind === "tingbog_akt" && row.pages.selected.length === 0) {
      servitutPdf = null;
   } else {
      servitutPdf = await cutExportPdf(row, blob);
   }
   if (!servitutPdf) {
      return null;
   }
   const safePdf = await rewritePdfForBundle(servitutPdf);
   try {
      return await withRowPaategningPages(safePdf, row, undefined, {
         includePdfBilag: true,
         labelPdfBilag: (index) => Localization.getFormattedText("Appendix {number}", { number: index }),
         labelGmlBilag: (index) => ({
            title: Localization.getFormattedText("Appendix {number}, GML file", { number: index }),
            body: Localization.getText("GML visualised in Mapit"),
         }),
      });
   } catch (error) {
      console.warn("[Servitutredegørelse] bundle append failed, using the servitut PDF", row.id, error);
      return safePdf;
   }
}

export async function buildServitutBundlePdf(
   rows: ServitutRow[],
   onProgress?: (state: ServitutExportProgress) => void | Promise<void>,
   cover?: { bfe?: string; address?: string },
): Promise<Blob> {
   const sections: BundleSection[] = [];
   for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      await onProgress?.({
         completedSteps: index,
         totalSteps: rows.length + 1,
         stage: "pdf",
         activeRowKey: row.id,
         documentIndex: index + 1,
         documentTotal: rows.length,
      });
      const title = [row.identity.datoloebenr || row.landRegistry.displayAktId, row.landRegistry.summary]
         .map((part) => `${part || ""}`.trim())
         .filter(Boolean)
         .join(" ");
      let pdf: Blob | null = null;
      try {
         pdf = await pdfForBundle(row);
      } catch (error) {
         console.warn("[Servitutredegørelse] bundle document missing", row.id, error);
      }
      sections.push({
         title: title || row.id,
         priority: `${row.landRegistry.priority || ""}`.trim(),
         pdf,
      });
   }
   await onProgress?.({
      completedSteps: rows.length,
      totalSteps: rows.length + 1,
      stage: "pdf",
      activeRowKey: null,
   });
   return buildNumberedBundlePdf(sections, {
      contents: Localization.getText("Easement review"),
      notFound: Localization.getText("Not found"),
      page: Localization.getText("Pages"),
      priority: Localization.getText("Priority"),
      servitut: Localization.getText("Easement"),
      general: Localization.getText("General information"),
      bfeLabel: Localization.getText("BFE"),
      bfeValue: cover?.bfe || "–",
      addressLabel: Localization.getText("Address"),
      addressValue: cover?.address || "",
      countLabel: Localization.getText("Number of easements"),
      countValue: `${rows.length}`,
      pagesLabel: Localization.getText("Pages"),
   });
}
